import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Button, Dropdown, Form } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import { useHistory, useLocation } from 'react-router-dom';

import {
  BG_1,
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  PRIMARY_COLOR,
  RADIUS_LG,
  WHITE_EMPHASIS,
} from 'components/upb_viewer_beta/css/betaTokens';
import { downloadFile, httpGet } from 'lib/networking/http';
import FsaLoader from 'components/shared/FsaLoader';
import {
  getURLWithSearchParams,
  MANUFACTURER_SKU_MAPPING_DP_PRODUCTS_EXPORT,
  MANUFACTURER_SKU_MAPPING_EZ_PASS_EXPORT,
  MANUFACTURER_SKU_MAPPING_MFC_DETAILS_EXPORT,
  MANUFACTURER_SKU_MAPPING_MPN_DP_MAPPING_EXPORT,
  MANUFACTURER_SKU_MAPPING_OVERVIEW,
  MANUFACTURER_SKU_MAPPING_OVERVIEW_EXPORT,
} from 'lib/networking/endpoints';
import InfluenceManufacturerTable from 'components/influence_sku_mapping/InfluenceManufacturerTable';
import InfluenceOverviewSummary from 'components/influence_sku_mapping/InfluenceOverviewSummary';
import InstacartDashboard from 'components/influence_sku_mapping/InstacartDashboard';
import { InfluenceStatus, InfluenceStatusOptions } from 'lib/enums/InfluenceStatus';
import SwalDialog from 'lib/utils/toast';

const TABS = [
  { key: 'sku-mapping', label: 'Influence' },
  { key: 'instacart', label: 'Instacart' },
];

// react-select styles tuned to sit inline with the search box: same 38px height and radius,
// single-line label, no separator, primary-colored focus and selected states.
const campaignStatusSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 38,
    fontSize: 13,
    borderRadius: RADIUS_LG,
    borderColor: state.isFocused ? PRIMARY_COLOR : BORDER_2,
    backgroundColor: BG_1,
    boxShadow: 'none',
    cursor: 'pointer',
    ':hover': { borderColor: PRIMARY_COLOR },
  }),
  valueContainer: provided => ({ ...provided, padding: '0 12px' }),
  singleValue: provided => ({ ...provided, color: FG_1, whiteSpace: 'nowrap' }),
  placeholder: provided => ({ ...provided, color: FG_3, whiteSpace: 'nowrap' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: provided => ({ ...provided, color: FG_3, padding: '0 8px' }),
  clearIndicator: provided => ({ ...provided, color: FG_3, padding: '0 4px' }),
  menu: provided => ({
    ...provided,
    fontSize: 13,
    borderRadius: RADIUS_LG,
    overflow: 'hidden',
    zIndex: 5,
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    // eslint-disable-next-line no-nested-ternary
    backgroundColor: state.isSelected ? PRIMARY_COLOR : state.isFocused ? BG_2 : BG_1,
    color: state.isSelected ? WHITE_EMPHASIS : FG_1,
  }),
};

/**
 * InfluenceSkuMappingContainer — the "Influence SKU mapping" dashboard.
 *
 * Manufacturers with an active Influence rebate campaign, bucketed by GMV-mapping
 * rate. The overview is small (tens of manufacturers), so band tiles and the
 * search box filter the already-loaded list client-side rather than refetching.
 */
function InfluenceSkuMappingContainer() {
  const history = useHistory();
  const { search } = useLocation();
  const rawTab = new URLSearchParams(search).get('tab') || 'sku-mapping';
  const activeTab = TABS.some(t => t.key === rawTab) ? rawTab : 'sku-mapping';
  const setActiveTab = key => {
    const params = new URLSearchParams(search);
    if (key === 'sku-mapping') {
      params.delete('tab');
    } else {
      params.set('tab', key);
    }
    const qs = params.toString();
    history.replace(`/influence-sku-mapping${qs ? `?${qs}` : ''}`);
  };

  const [query, setQuery] = useState('');
  // Default to Active so the dashboard lands on the live campaign scope (carries into drill-ins).
  const [campaignStatus, setCampaignStatus] = useState(InfluenceStatus.ACTIVE);

  // The selected product campaign status rescopes the whole dashboard server-side: cohort
  // restricted to matching-influence_status manufacturers, and row stats + cards scoped to that
  // status. Omit the param when unset so the overview returns the full ACTIVE+DRAFT cohort.
  const overviewUrl = useMemo(
    () =>
      getURLWithSearchParams(
        MANUFACTURER_SKU_MAPPING_OVERVIEW,
        campaignStatus ? { campaign_product_status: campaignStatus } : {},
      ),
    [campaignStatus],
  );

  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Changing the status filter fires overlapping overview GETs; a monotonic request id lets only
  // the latest one commit state, so a slow earlier response can't overwrite a newer status's data.
  // Kept local here rather than changing the shared useGet hook (used app-wide).
  const requestIdRef = useRef(0);

  const fetchOverview = useCallback(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError(false);
    httpGet(overviewUrl)
      .then(res => {
        if (requestId !== requestIdRef.current) return; // superseded by a newer request
        if (res.status === 200) setData(res.data);
      })
      .catch(() => {
        if (requestId === requestIdRef.current) setError(true);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [overviewUrl]);

  useEffect(() => {
    if (activeTab === 'sku-mapping') fetchOverview();
  }, [fetchOverview, activeTab]);

  const manufacturers = useMemo(() => data?.manufacturers || [], [data]);

  // The campaign-status scope is applied server-side (it rescopes the cohort, every row's stats,
  // the cards, and the band summary together), so the only client-side filter left is the search
  // box -- re-filtering by status here would hide rows the cards/bands still count.
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return manufacturers;
    return manufacturers.filter(row => (row.name || '').toLowerCase().includes(needle));
  }, [manufacturers, query]);

  // Carry the selected campaign status into the drill-in so it scopes the same way.
  const onAnalyze = manufacturerId =>
    history.push(
      getURLWithSearchParams(
        `/influence-sku-mapping/manufacturers/${manufacturerId}`,
        campaignStatus ? { campaign_product_status: campaignStatus } : {},
      ),
    );

  const statusSuffix = campaignStatus ? `_${campaignStatus.toLowerCase()}` : '';

  // Build an export URL scoped to the selected status, optionally including all DP active_statuses
  // (Discontinued/null) instead of just live ones, so the download matches the filtered dashboard.
  const exportUrl = (endpoint, activeOnly) =>
    getURLWithSearchParams(endpoint, {
      ...(campaignStatus ? { campaign_product_status: campaignStatus } : {}),
      ...(activeOnly ? { active_only: true } : {}),
    });

  // One in-flight guard across the export dropdown; download() sets the filename + error toast.
  const [exporting, setExporting] = useState(false);
  const runExport = (endpoint, baseName, { activeOnly = false, failLabel } = {}) => {
    setExporting(true);
    const activeSuffix = activeOnly ? '_active_only' : '';
    downloadFile(exportUrl(endpoint, activeOnly), `${baseName}${statusSuffix}${activeSuffix}.xlsx`)
      .catch(() =>
        SwalDialog('error', `Could not export ${failLabel}. Please try again.`, 'Export failed'),
      )
      .finally(() => setExporting(false));
  };

  const onExportCampaignProducts = () =>
    runExport(MANUFACTURER_SKU_MAPPING_OVERVIEW_EXPORT, 'influence_campaign_products', {
      failLabel: 'campaign products',
    });
  const onExportDpProducts = activeOnly =>
    runExport(MANUFACTURER_SKU_MAPPING_DP_PRODUCTS_EXPORT, 'influence_dp_tam_products', {
      activeOnly,
      failLabel: 'DP TAM products',
    });
  const onExportMpnDpMapping = activeOnly =>
    runExport(MANUFACTURER_SKU_MAPPING_MPN_DP_MAPPING_EXPORT, 'influence_campaign_mpn_dp_mapping', {
      activeOnly,
      failLabel: 'the MPN → DP mapping',
    });
  const onExportMfcDetails = () =>
    runExport(MANUFACTURER_SKU_MAPPING_MFC_DETAILS_EXPORT, 'influence_mfc_details', {
      failLabel: 'the MFC details',
    });
  const onExportEzPass = () =>
    runExport(MANUFACTURER_SKU_MAPPING_EZ_PASS_EXPORT, 'influence_ez_pass', {
      failLabel: 'the EZ Pass report',
    });

  // Initial Influence load: full-view loader only when on the Influence tab and no data yet.
  if (activeTab === 'sku-mapping' && loading && !data) {
    return (
      <div className={css(styles.loadingWrap)}>
        <FsaLoader fullView size={96} label="Loading dashboard…" />
      </div>
    );
  }

  return (
    <div className={css(styles.page)}>
      <div className={css(styles.crumbs)}>FSA · Dashboards · Influence SKU mapping</div>

      <div className={css(styles.tabBar)} role="tablist">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            className={css(styles.tab, activeTab === t.key && styles.tabActive)}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'instacart' && <InstacartDashboard />}

      {activeTab === 'sku-mapping' && (
        <>
          <div className={css(styles.overviewTag)}>OVERVIEW</div>
          <div className={css(styles.titleRow)}>
            <h1 className={css(styles.title)}>Influence readiness by manufacturer</h1>
            <div className={css(styles.exportActions)}>
              <Dropdown>
                <Dropdown.Toggle variant="outline-primary" size="sm" disabled={exporting}>
                  <FontAwesomeIcon icon={faDownload} className="mr-1" />
                  {exporting ? 'Exporting…' : 'Export'}
                </Dropdown.Toggle>
                <Dropdown.Menu align="right">
                  <Dropdown.Item onClick={onExportCampaignProducts}>
                    Campaign products
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onExportDpProducts(false)}>
                    DP TAM products
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onExportDpProducts(true)}>
                    DP TAM products (active only)
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  {campaignStatus && (
                    <Dropdown.Item onClick={() => onExportMpnDpMapping(false)}>
                      MPN → DP mapping
                    </Dropdown.Item>
                  )}
                  {campaignStatus && (
                    <Dropdown.Item onClick={() => onExportMpnDpMapping(true)}>
                      MPN → DP mapping (active only)
                    </Dropdown.Item>
                  )}
                  {/* No status -> a two-sheet (Active + Draft) workbook. */}
                  <Dropdown.Item onClick={onExportMfcDetails}>MFC details</Dropdown.Item>
                  {/* No status -> active + draft MFRs in one workbook (a sheet each). */}
                  <Dropdown.Item onClick={onExportEzPass}>EZ Pass</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <p className={css(styles.subtitle)}>
            Mapped SKUs as a share of TAM (a manufacturer&apos;s branded SKUs on influence-enabled
            distributors that are mapped or have a V2-mapper suggested match). Search by name to
            filter the list.
          </p>

          {/* A failed (re)fetch is a non-blocking banner, not a full-screen takeover:
          the filter and table stay mounted so the user can change status or Retry. */}
          {error && (
            <Alert variant="danger" className={css(styles.errorBanner)}>
              <span>Failed to load the dashboard.</span>
              <Button variant="outline-danger" size="sm" onClick={fetchOverview}>
                Retry
              </Button>
            </Alert>
          )}

          <InfluenceOverviewSummary
            totals={data?.totals}
            loading={loading}
            campaignStatus={campaignStatus}
          />

          <div className={css(styles.listHead)}>
            <div>
              <h2 className={css(styles.listTitle)}>Influence enabled manufacturer list</h2>
              <div className={css(styles.listMeta)}>
                {`Showing ${filtered.length} of ${manufacturers.length} — highest rate first`}
              </div>
            </div>
            <div className={css(styles.filters)}>
              <Select
                className={css(styles.select)}
                classNamePrefix="campaign-status-filter"
                options={InfluenceStatusOptions}
                value={
                  InfluenceStatusOptions.find(option => option.value === campaignStatus) || null
                }
                onChange={option => setCampaignStatus(option?.value || '')}
                styles={campaignStatusSelectStyles}
                isClearable
                placeholder="All influence statuses"
                aria-label="Influence status"
              />
              <Form.Control
                className={css(styles.search)}
                type="search"
                placeholder="Filter manufacturers"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
          </div>

          {/* On a status-filter refetch the table rows are rescoped server-side; dim the stale
          list and float a spinner so it reads as refreshing rather than frozen. */}
          <div className={css(styles.tableRegion)}>
            <div className={css(loading && styles.tableDimmed)}>
              <InfluenceManufacturerTable rows={filtered} onAnalyze={onAnalyze} />
            </div>
            {loading && (
              <div className={css(styles.tableSpinner)}>
                <FsaLoader size={48} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  page: { background: BG_2, minHeight: '100%', padding: '28px 32px 48px' },
  loadingWrap: { background: BG_2 },
  crumbs: { fontSize: 12.5, color: FG_3, marginBottom: 12 },
  tabBar: {
    display: 'flex',
    gap: 4,
    borderBottom: `1px solid ${BORDER_2}`,
    marginBottom: 20,
  },
  tab: {
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    padding: '8px 14px',
    marginBottom: -1,
    fontSize: 14,
    fontWeight: 600,
    color: FG_2,
    cursor: 'pointer',
    ':hover': { color: FG_1 },
    ':focus': { outline: 'none', boxShadow: 'none' },
  },
  tabActive: {
    color: PRIMARY_COLOR,
    borderBottomColor: PRIMARY_COLOR,
  },
  overviewTag: { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: PRIMARY_COLOR },
  title: { fontSize: 26, fontWeight: 700, color: FG_1, margin: '6px 0 6px' },
  subtitle: { fontSize: 14, color: FG_2, maxWidth: 760, marginBottom: 22 },
  listHead: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    margin: '30px 0 14px',
  },
  listTitle: { fontSize: 18, fontWeight: 700, color: FG_1, marginBottom: 4 },
  listMeta: { fontSize: 13, color: FG_3 },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  exportActions: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  filters: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'nowrap' },
  search: {
    width: 240,
    height: 38,
    fontSize: 13,
    borderRadius: RADIUS_LG,
    border: `1px solid ${BORDER_2}`,
    background: BG_1,
  },
  select: { width: 240 },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  tableRegion: { position: 'relative' },
  // Dim + lock the stale table during a refetch (opacity/pointer-events only, no color token).
  tableDimmed: { opacity: 0.45, pointerEvents: 'none', transition: 'opacity 120ms ease' },
  tableSpinner: {
    position: 'absolute',
    top: 48,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    zIndex: 2,
  },
});

export default InfluenceSkuMappingContainer;
